import { QueryClient, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { MAX_NAVIGATION_FOLDERS_DEPTH, SpaceKind, SpaceNavigationFolder } from 'shared';

import { BackendRoute } from '../../../config/index';
import { QueryKey } from '../../../constants/index';
import { useAppQuery } from '../../../hooks/use-app-query.hook';
import { ApiService } from '../../../services/index';
import { selectedCompanyStorage } from '../../../storage/company/selected-company.util';
import { SPACES_STALE_TIME_MS } from '../constants/spaces-query.const';
import { SpaceRef } from '../types/data-room.types';
import { inVaultNameOrder } from '../vault/utils/vault-folder-order.util';
import { DataRoomViewer, useDataRoomViewer } from './use-recipient-view.hook';

type FolderLevel = {
  kind: SpaceKind;
  parentFolderId: null | string;
  // Not sent for the Vault, which the session's company identifies.
  spaceId: string;
};

const NO_FOLDERS: SpaceNavigationFolder[] = [];

/**
 * Which route a level comes from. Both spellings of the tree are the same shape, but they are read
 * through different endpoints — the owner's module route and the recipient's room route — and one
 * account can be an owner in one company and a recipient in another, so the cache has to keep them
 * apart.
 */
export enum FolderTreeScope {
  OWNER = 'owner',
  RECIPIENT = 'recipient',
}

// Each level comes with the one below it, so a row's children are cached before it opens. The
// sidebar and the browser share this key, so a level loaded by either is ready for the other.
const buildQueryParams = ({ kind, parentFolderId, spaceId }: FolderLevel) => ({
  depth: MAX_NAVIGATION_FOLDERS_DEPTH,
  kind,
  ...(kind === SpaceKind.VAULT ? {} : { spaceId }),
  ...(parentFolderId ? { parentFolderId } : {}),
});

const folderLevelQueryKey = (companyId: string, scope: FolderTreeScope, level: FolderLevel) => [
  QueryKey.GET_SPACE_NAVIGATION_FOLDERS,
  companyId,
  scope,
  buildQueryParams(level),
];

/**
 * Refreshes the levels the sidebar holds for one space, and none of another space's. A new or
 * deleted folder can show anywhere in its space's cached levels — its parent's list, the parent's
 * `hasChildren` one level up, the children preloaded with a level — but never in another space.
 */
export const invalidateSpaceFolderLevels = (
  queryClient: QueryClient,
  companyId: string,
  space: SpaceRef,
) =>
  queryClient.invalidateQueries({
    predicate: ({ queryKey: [key, keyCompanyId, , params] }) => {
      if (key !== QueryKey.GET_SPACE_NAVIGATION_FOLDERS || keyCompanyId !== companyId) {
        return false;
      }

      const level = params as ReturnType<typeof buildQueryParams>;

      return level.kind === space.kind && 'spaceId' in level && level.spaceId === space.id;
    },
  });

// What the sidebar already knows of a level, if anything, without asking the server.
export const readCachedFolderLevel = (
  queryClient: QueryClient,
  companyId: string,
  scope: FolderTreeScope,
  level: FolderLevel,
): SpaceNavigationFolder[] | undefined =>
  queryClient.getQueryData<SpaceNavigationFolder[]>(folderLevelQueryKey(companyId, scope, level));

/**
 * Caches a level that arrived with its parent. Stored as stale (`updatedAt: 0`): the copy has no
 * children, so the row's own request must still run on mount to bring the level below.
 */
export const seedFolderLevel = (
  queryClient: QueryClient,
  companyId: string,
  scope: FolderTreeScope,
  level: FolderLevel,
  folders: SpaceNavigationFolder[],
) => {
  const queryKey = folderLevelQueryKey(companyId, scope, level);

  if (queryClient.getQueryData(queryKey)) return;

  queryClient.setQueryData<SpaceNavigationFolder[]>(queryKey, folders, { updatedAt: 0 });
};

const fetchLevel = (scope: FolderTreeScope, level: FolderLevel) =>
  scope === FolderTreeScope.RECIPIENT
    ? ApiService.get<SpaceNavigationFolder[]>(
        { endpoint: BackendRoute.MY_DATA_ROOMS, routePath: [level.spaceId, 'folders'] },
        {
          queryParams: level.parentFolderId ? { parentFolderId: level.parentFolderId } : {},
        },
      )
    : ApiService.get<SpaceNavigationFolder[]>(
        { endpoint: BackendRoute.SPACES, routePath: ['navigation', 'folders'] },
        { queryParams: buildQueryParams(level) },
      );

export const useSpaceNavigationFolders = (
  level: FolderLevel,
  { enabled = true }: { enabled?: boolean } = {},
) => {
  // Only keys the cache: both routes take the company from the session or from the access row.
  const companyId = selectedCompanyStorage(({ companyId: selected }) => selected);
  const queryClient = useQueryClient();
  // Which route to use is decided before the request, never by trying the owner's and reading the
  // refusal: the module matrix would answer 403 and a recipient would be left without folders.
  const viewer = useDataRoomViewer();
  // The Vault is not a room that was shared: even for a Stakeholder, who is a recipient everywhere
  // else, its levels come from the module route, which narrows them to the folders that are theirs.
  const scope =
    viewer === DataRoomViewer.RECIPIENT && level.kind !== SpaceKind.VAULT
      ? FolderTreeScope.RECIPIENT
      : FolderTreeScope.OWNER;

  const { data, isError, isPending, refetch } = useAppQuery({
    queryKey: folderLevelQueryKey(companyId, scope, level),
    // Seeding here runs once per response, not on every render that sees the data.
    queryFn: async () => {
      const folders = await fetchLevel(scope, level);

      for (const folder of folders) {
        if (folder.children) {
          seedFolderLevel(
            queryClient,
            companyId,
            scope,
            { ...level, parentFolderId: folder.id },
            folder.children,
          );
        }
      }

      return folders;
    },
    enabled: enabled && !!companyId && viewer !== DataRoomViewer.UNRESOLVED,
    staleTime: SPACES_STALE_TIME_MS,
  });

  const retry = useCallback(() => void refetch(), [refetch]);
  // A Vault level reads A to Z with General and Documents first, as the Vault's table lists it.
  // Ordered on read, not on fetch, so a level seeded with its parent is ordered as well.
  const isVault = level.kind === SpaceKind.VAULT;
  const folders = useMemo(() => {
    if (!data) return NO_FOLDERS;

    return isVault ? inVaultNameOrder(data) : data;
  }, [data, isVault]);

  return {
    folders,
    // A level that failed has no folders to show, which is not the same as an empty level.
    isError: enabled && isError && !data,
    isPending: enabled && (viewer === DataRoomViewer.UNRESOLVED || isPending),
    retry,
  };
};
